/*
 * Plotter component utilizing react-plotly.js
 * Documentation at https://github.com/plotly/react-plotly.js/
 */
import React, { Component } from 'react';
import Plot from "react-plotly.js";
import axios from 'axios';
import Papa from 'papaparse';
import { Row } from "antd";
import { mean, compact } from 'lodash';

/* By default, Axios or xhr.js will reject a web-service response if a 404 error is returned.  When this happens, it will
 * throw an error which halts the program.  However, for the events web-service, a 404 error is a valid return just having no
 * data therefore we want to keep program execution so rather than throw away the promise we return a boolean variable of false.
 */
axios.interceptors.response.use(function (response) {
    return response;
}, function (error) {
    if (404 === error.response.status) {
        return false;
    } else {
        return Promise.reject( error );
    }
});

/*
 * Default colors for the graphs - currently there are 15 unique colors corresponding to each station graphed;
 * If desired, we can add more colors to accommodate more stations.
 */
const DEFAULT_COLORS = [
    '#1f77b4',
    '#ff7f0e',
    '#2ca02c',
    '#9467bd',
    '#8c564b',
    '#e377c2',
    '#7f7f7f',
    '#bcbd22',
    '#17becf',
    '#bacbf3',
    '#a91ab1',
    '#c4cdbf',
    '#28eebb',
    '#44896e',
    '#0614d6'
];

/*
 * Get the timeseries data for a list of stations and then provide callback to graph the data
 */
function get_timeseries_data( stations, reference_frame, processing, annotations, view, callback ) {
    /*
     * Safety check in case stations has no value
     */
    if( stations.length <= 0 )
        return callback([]);

    let result = [],
        urls = [],
        counter = 0,
        process_maintenance_events = annotations.indexOf( 'maintenance-events' ) >= 0;

    // Queues data from web-services
    for( let i = 0; i < stations.length; i++ ) {
        const item = stations[ i ],
            url_timeseries = 'https://web-services.unavco.org/gps/data/position/' + item + '/v3?analysisCenter=cwu&referenceFrame=' + reference_frame + '&dataPostProcessing=' + processing + '&report=short&refCoordOption=from_analysis_center',
            url_maintenance_events = 'http://web-services-beta.int.unavco.org/internal/events/eq/beta?station=' + item;

        let timeseries, events = false;

        //+ '&starttime=2017-12-01&endtime=2018-11-01'

        timeseries = axios.get( url_timeseries, {
            params : {
                station_id : item,
                type : 'csv-time-series'
            }});

        if( process_maintenance_events ) {
            events = axios.get( url_maintenance_events, {
                params: {
                    type: 'events'
                }
            });
        }

        urls.push( [ timeseries, events, i ] );

        // Creates a temporary index placement for station's array
        result.push( item );
    }

    // Define functions here rather than inside for-loop to avoid warnings
    const process = ( response ) => {
            const timeseries_data = response[ 0 ].data,
                maintenance_events_data = response[ 1 ] ? response[ 1 ].data : null,
                station_id = response[ 0 ].config.params.station_id,
                station_order = response[ 2 ];

            // Parses the data and convert them into appropriate format for plotly.js.  We are splicing the result in order to ensure
            // the display order of the arrays
            if( view === 'single' ) {
                result.splice(station_order, 1, make_plotly_traces(station_id, timeseries_data, maintenance_events_data, annotations, station_order));
            } else {
                result.splice(station_order, 1, make_plotly_traces(station_id, timeseries_data, maintenance_events_data, annotations, station_order));
            }

            counter++;
        },
        success = () => {
            // Updates the state after processing through all the urls
            if( counter ===  urls.length ) {
                callback(result);
            }
        };

    // Processes the data from the web-services
    for( let i = 0; i < urls.length; i++ ) {
        axios.all(  urls[ i ]  )
            .then( process )
            .catch( ( error ) => {
                console.log('Error fetching timeseries data' );
                console.log( error );
            })
            .then( success )
    }
}

/*
 * Converts web-services data into Plotly.js graph points
 */
function make_plotly_traces( station_id, data, events, annotations, counter ){
    /*
     * Parses the timeseries data (CSV format) from UNAVCO's web-services
     */
    const parse_time_series  = ( data ) => {
        const papa_config = {
            comments : '#',
            header : true,
            trimHeaders : true,
            skipEmptyLines : true,
            dynamicTyping : true
        };

        let parsed_data =  Papa.parse( data, papa_config ),
            results = {
                datetime : [],
                deltaN : [],
                deltaE : [],
                deltaU : [],
                stdDevN_Upper : [],
                stdDevN_Lower : [],
                stdDevE_Upper : [],
                stdDevE_Lower : [],
                stdDevU_Upper : [],
                stdDevU_Lower : [],
            };

        for (let i = 0; i < parsed_data.data.length; i++ ) {
            results.datetime.push( parsed_data.data[ i ][ 'Datetime' ] );
            results.deltaN.push( parseFloat( parsed_data.data[ i ][ 'delta N' ] ) );
            results.deltaE.push( parseFloat( parsed_data.data[ i ][ 'delta E' ] ) );
            results.deltaU.push( parseFloat( parsed_data.data[ i ][ 'delta U' ] ) );
            results.stdDevN_Upper.push( parseFloat( parsed_data.data[ i ][ 'delta N' ] + parsed_data.data[ i ][ 'Std Dev N' ] ) );
            results.stdDevN_Lower.push( parseFloat( parsed_data.data[ i ][ 'delta N' ] - parsed_data.data[ i ][ 'Std Dev N' ] ) );
            results.stdDevE_Upper.push( parseFloat( parsed_data.data[ i ][ 'delta E' ] + parsed_data.data[ i ][ 'Std Dev E' ] ) );
            results.stdDevE_Lower.push( parseFloat( parsed_data.data[ i ][ 'delta E' ] - parsed_data.data[ i ][ 'Std Dev E' ] ) );
            results.stdDevU_Upper.push( parseFloat( parsed_data.data[ i ][ 'delta U' ] + parsed_data.data[ i ][ 'Std Dev U' ] ) );
            results.stdDevU_Lower.push( parseFloat( parsed_data.data[ i ][ 'delta U' ] - parsed_data.data[ i ][ 'Std Dev U' ] ) );
        }

        return results;
    };

    // Parses time series data into javascript Object
    data = parse_time_series( data );

    // Counter is used to set the plot colors; Once it reaches the max default colors, it will start again from the beginning.
    counter = counter < DEFAULT_COLORS.length ? counter : counter % DEFAULT_COLORS.length;

    const color = DEFAULT_COLORS[ counter ],
        trace_default = {
            x : data.datetime,
            /*type : 'scatter',
            mode : 'markers',*/
            type : 'lines',
            connectgaps : true,
            marker: {
                color : color
            },
            legendgroup: station_id,
            name : station_id
        },
        trace_north = Object.assign( {}, trace_default, {
            y : data.deltaN,
            yaxis : 'y1'
        }),
        trace_east = Object.assign( {}, trace_default, {
            y : data.deltaE,
            yaxis : 'y2',
            showlegend : false
        }),
        trace_up = Object.assign( {}, trace_default, {
            y : data.deltaU,
            yaxis : 'y3',
            showlegend : false
        });

    // Assigns north, south, and up data to graph
    let result = [ trace_north, trace_east, trace_up ];

    /*
     * Creates error traces if errorbars are set
     */
    if( annotations.indexOf( 'errorbars' ) >= 0 ){
        /*
         * Function to create plots for error traces
         */
        const make_error_traces = ( upper, lower, axis ) => {
            const trace_error_default = {
                    x : data.datetime,
                    type : 'lines',
                    showlegend : false,
                    hoverinfo : 'skip',
                    line : {
                        width : 0
                    },
                    connectgaps : true,
                    legendgroup: station_id
                },
                trace_error_upper = Object.assign( {}, trace_error_default, {
                    y : upper,
                    yaxis : axis
                }),
                trace_error_lower = Object.assign( {}, trace_error_default, {
                    y : lower,
                    yaxis : axis,
                    fillcolor : 'rgba(68, 68, 68, 0.3)',
                    fill : 'tonexty'
                });

            return [ trace_error_upper, trace_error_lower ];
        };

        result = result.concat( make_error_traces( data.stdDevN_Upper, data.stdDevN_Lower, 'y' ),
            make_error_traces( data.stdDevE_Upper, data.stdDevE_Lower, 'y2' ),
            make_error_traces( data.stdDevU_Upper, data.stdDevU_Lower, 'y3' ) );
    }

    /*
     * Creates maintenance traces if maintenance-events are set
     */
    if( ( annotations.indexOf( 'maintenance-events' ) >= 0 )  && ( events ) ){
        /*
         Create maintenance events traces
         */
        const make_maintenance_events_traces = ( data, axis, y ) => {
            const offsets = data[ 0 ].offsets;
            const station_code = data[0].station_code;
            let result = {
                x : [],
                y : [],
                yaxis : axis,
                text : [],
                marker: {
                    size : 20,
                    opacity : 1,
                    symbol : 'circle-cross-open',
                    color : '#d62728'
                },
                mode : 'markers',
                legendgroup: station_id,
                showlegend : false,
                hoverinfo : 'text',
                namelength : 5
            };

            for( let i = 0; i < offsets.length; i++ ) {
                result.x.push( offsets[ i ].date ) ;
                result.y.push( y );
                result.text.push(`${station_code}: ${offsets[i].description}`);
            }

            return result;
        };

        // Put the y coordinate of the events at the mean level of the graph; The compact function removes null values
        result = result.concat( make_maintenance_events_traces( events, 'y', mean( compact( trace_north.y ) ) ),
            make_maintenance_events_traces( events, 'y2', mean( compact( trace_east.y ) ) ),
            make_maintenance_events_traces( events, 'y3', mean( compact( trace_up.y ) ) ) );
    }

    // Return array of plot-points to be graphed
    return result;
}

/*
 * Plotly component
 */
const Plotter = ( props ) => {

    if( props.data.length <= 0 ){
        return ( <div>&nbsp;</div> );
    }

    /*
     *Layout object for plotly.js see documentation at https://plot.ly/javascript/reference/#layout
     */
    let layout = {
        autosize : true,
        title : props.title,
        margin : {
            t : 40,
            r : 30,
            b : 30,
            l : 60
        },
        font : {
            family : 'Roboto, Helvetica, sans-serif'
        },
        showlegend : true,
        legend : {
            orientation: 'h',
            yanchor : 'top',
            xanchor : 'center',
            x : .5,
            y : 1,
            traceorder : 'normal'
        },
        xaxis : {
            type : 'date',
            //rangeslider : {},
            anchor : 'y3',
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false
        },
        yaxis : {
            title: 'North (m)',
            domain : [ .66, 1 ],
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false,
            //range : [2, 5]
        },
        yaxis2 : {
            title : 'East (m)',
            domain : [ .33, .65 ],
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false,
        },
        yaxis3 : {
            title : 'Height (m)',
            domain : [ 0, .32 ],
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false,
        }
    };

    let config = {
        toImageButtonOptions: {
            format : 'svg',
            filename : props.title,
            height : 1280,
            width : 1000,
            scale : 1
        }
    };

    let result = [],
        yaxis_min = 0,
        yaxis_max = 0,
        yaxis2_min = 0,
        yaxis2_max = 0,
        yaxis3_min = 0,
        yaxis3_max = 0;

    const clone = props.data.slice(0);

    // Converts array to map all stations' data into one graph and calculates min/max range excluding errorbars
    clone.map( subarray => subarray.map( function( item ) {
        result.push( item );

        if( ( item.name ) && ( item.yaxis === 'y1' ) ){
            yaxis_min = yaxis_min <= Math.min( ...item.y ) ? yaxis_min : Math.min( ...item.y );
            yaxis_max = yaxis_max >= Math.max( ...item.y ) ? yaxis_max : Math.max( ...item.y );
        }

        if( ( item.name ) && ( item.yaxis === 'y2' ) ){
            yaxis2_min = yaxis2_min <= Math.min( ...item.y ) ? yaxis2_min : Math.min( ...item.y );
            yaxis2_max = yaxis2_max >= Math.max( ...item.y ) ? yaxis2_max : Math.max( ...item.y );
        }

        if( ( item.name ) && ( item.yaxis === 'y3' ) ){
            yaxis3_min = yaxis3_min <= Math.min( ...item.y ) ? yaxis3_min : Math.min( ...item.y );
            yaxis3_max = yaxis3_max >= Math.max( ...item.y ) ? yaxis3_max : Math.max( ...item.y );
        }

        return true;
    }));

    layout.yaxis.range = [ yaxis_min, yaxis_max ];
    layout.yaxis2.range = [ yaxis2_min, yaxis2_max ];
    layout.yaxis3.range = [ yaxis3_min, yaxis3_max ];

    return (
        <Row gutter = { 0 } type = "flex" justify = "center">
            <Plot
                data = { result }
                layout = { layout }
                config = { config }
                useResizeHandler = { true }
                className = "responsive-plot-100"
            />
        </Row>
    )
};

/*
 * Plotly component - single graphs for each station
 */
const PlotterSingle = ( props ) => {
    const layout = {
        autosize : true,
        margin : {
            t : 40,
            b : 30,
            l : 60,
            r : 30
        },
        showlegend : false,
        legend : {
            'orientation': 'h'
        },
        font : {
            family : 'Roboto, Helvetica, sans-serif'
        },
        xaxis : {
            type : 'date',
            anchor : 'y3',
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false
        },
        yaxis : {
            title: 'North (m)',
            domain : [ .66, 1 ],
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false,
        },
        yaxis2 : {
            title : 'East (m)',
            domain : [ .33, .66 ],
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false,
        },
        yaxis3 : {
            title : 'Height (m)',
            domain : [ 0, .33 ],
            gridcolor : '#eee',
            gridwidth : 1,
            zeroline : false,
        }
    };

    if( props.data.length <= 0 ){
        return ( <div>&nbsp;</div> );
    }

    return(
        props.data.map( x => {
            const station_id = x[ 0 ].name.substring( 0, 4 );
            const plot_layout = Object.assign( { title : station_id + ' ' + props.title }, layout );
            const plot_config = {
                toImageButtonOptions : {
                    format : 'svg',
                    width : 1280,
                    filename : station_id + ' ' + props.title,
                    height : 450,
                    scale : 1
                }
            };

            return (
                <Row key = { station_id } gutter = { 0 } type = "flex" justify = "center">
                    <Plot
                        data = { x }
                        layout = { plot_layout }
                        config = { plot_config }
                        useResizeHandler = { true }
                        className = "responsive-plot"
                    />
                </Row>
            )
        })
    )
};

/*
 * Container object for multiple plotting tools (potentially)
 */
class PlotterContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data : [],
            frames : []
        };
    }

    componentDidUpdate( prevProps ) {
        const params = this.props.params;

        if( this.props !== prevProps ) {
            get_timeseries_data( params.stations, params.reference_frame, params.processing, params.annotations, params.view, data => {
                this.setState({ data: data });
            });
        }
    }

    capitalizeFirstLetter( string ) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    render(){
        let title = this.props.params.reference_frame + ' Data'

        if( this.props.params.processing === 'uncleaned' ){
            title = this.props.params.reference_frame + ' Data'
        } else {
            title = this.props.params.reference_frame + ' - ' + this.capitalizeFirstLetter( this.props.params.processing ) + ' Data'
        }

        if( this.props.params.view === 'single' ){
            return (
                <PlotterSingle data = { this.state.data } title = { title }/>
            )
        } else {
            return (
                <Plotter data = { this.state.data } title = { title }/>
            )
        }

    }
}

export default PlotterContainer;


// WEBPACK FOOTER //
// ./src/components/Plotter/Plotter.js